import React, { useState, useEffect, useRef } from "react";
import {
  MapContainer,
  TileLayer,
  Marker,
  Polyline,
  Popup,
  useMap,
} from "react-leaflet";
import L from "leaflet";
import "leaflet/dist/leaflet.css";
import Form from "react-bootstrap/Form";
import Button from "react-bootstrap/Button";
import Row from "react-bootstrap/Row";
import Col from "react-bootstrap/Col";
import Container from "react-bootstrap/Container";
import RecapModal from "./RecapModal";
import logo from "../images/logo.png";
import { setOptions, importLibrary } from "@googlemaps/js-api-loader";

// Icône leaflet par défaut
const departIcon = new L.Icon({
  iconUrl: "https://unpkg.com/leaflet@1.9.4/dist/images/marker-icon.png",
  iconRetinaUrl:
    "https://unpkg.com/leaflet@1.9.4/dist/images/marker-icon-2x.png",
  shadowUrl: "https://unpkg.com/leaflet@1.9.4/dist/images/marker-shadow.png",
  iconSize: [25, 41],
  iconAnchor: [12, 41],
  popupAnchor: [1, -34],
  shadowSize: [41, 41],
});

function AutoOpenPopup({ popupRef }) {
  const map = useMap();
  useEffect(() => {
    if (popupRef.current) popupRef.current.openOn(map);
  }, [popupRef, map]);
  return null;
}

function FitToRoute({ route }) {
  const map = useMap();

  useEffect(() => {
    if (!route || route.length < 2) return;

    const bounds = L.latLngBounds(route);
    const isMobile = window.innerWidth < 992;

    map.fitBounds(bounds, {
      paddingTopLeft: isMobile ? [20, 20] : [350, 40],
      paddingBottomRight: isMobile ? [20, 20] : [40, 40],
      animate: true,
      maxZoom: 13,
    });

    const zoom = map.getZoom();
    if (zoom > 13) map.setZoom(13);
  }, [route, map]);

  return null;
}

const TARIF_KM = 2;

export default function FormBis() {
  const [formError, setFormError] = useState("");

  const [depart, setDepart] = useState("");
  const [arrivee, setArrivee] = useState("");

  // Suggestions (Place Predictions)
  const [suggestDepart, setSuggestDepart] = useState([]);
  const [suggestArrivee, setSuggestArrivee] = useState([]);

  const [coordsDepart, setCoordsDepart] = useState(null);
  const [coordsArrivee, setCoordsArrivee] = useState(null);

  const [route, setRoute] = useState(null);
  const [distance, setDistance] = useState(0);
  const [duree, setDuree] = useState(0);
  const [prix, setPrix] = useState(0);

  const [date, setDate] = useState("");
  const [heure, setHeure] = useState("");
  const [demandes, setDemandes] = useState("");

  const [showRecap, setShowRecap] = useState(false);
  const [step, setStep] = useState(1);

  const [name, setName] = useState("");
  const [firstname, setFirstname] = useState("");
  const [email, setEmail] = useState("");
  const [phone, setPhone] = useState("");

  const [booking, setBooking] = useState("");

  const [serviceType, setServiceType] = useState("Transfert Aéroport");
  const [passagers, setPassagers] = useState(2);
  const [bagages, setBagages] = useState(2);
  const [options, setOptionsState] = useState({
    wifi: true,
    eau: true,
    cafe: true,
  });

  const popupRef = useRef();

  // Debounce
  const DEBOUNCE_DELAY = 450;
  const debounceDepart = useRef(null);
  const debounceArrivee = useRef(null);

  // Places New
  const [placesReady, setPlacesReady] = useState(false);
  const placesLibRef = useRef(null);

  // Session tokens (1 par champ, reset après sélection)
  const tokenDepartRef = useRef(null);
  const tokenArriveeRef = useRef(null);

  // Chargement Places (New API)
  useEffect(() => {
    let mounted = true;

    (async () => {
      try {
        const key = process.env.REACT_APP_GOOGLE_MAPS_API_KEY;
        if (!key) {
          console.error("REACT_APP_GOOGLE_MAPS_API_KEY manquant");
          return;
        }

        setOptions({
          key,
          v: "weekly",
          language: "fr",
          region: "FR",
        });

        const places = await importLibrary("places");
        if (!mounted) return;

        placesLibRef.current = places;
        setPlacesReady(true);
      } catch (err) {
        console.error("Erreur chargement Google Places:", err);
        setPlacesReady(false);
      }
    })();

    return () => {
      mounted = false;
    };
  }, []);

  const labelFromPrediction = (p) => {
    // sécurisation multi-formats
    return (
      p?.text?.text ||
      p?.structuredFormat?.mainText?.text ||
      p?.description ||
      p?.placeId ||
      "Résultat"
    );
  };

  // fetch suggestions (AutocompleteSuggestion)
  const fetchSuggestions = async (value, type) => {
    const q = (value || "").trim();
    if (!placesReady || q.length < 3) {
      if (type === "depart") setSuggestDepart([]);
      else setSuggestArrivee([]);
      return;
    }

    const { AutocompleteSuggestion, AutocompleteSessionToken } =
      placesLibRef.current || {};

    if (!AutocompleteSuggestion || !AutocompleteSessionToken) return;

    const tokenRef = type === "depart" ? tokenDepartRef : tokenArriveeRef;
    if (!tokenRef.current) tokenRef.current = new AutocompleteSessionToken();

    try {
      const res = await AutocompleteSuggestion.fetchAutocompleteSuggestions({
        input: q,
        sessionToken: tokenRef.current,
        // optionnel: country/region bias possible ici plus tard
      });

      const predictions =
        (res?.suggestions || [])
          .map((s) => s.placePrediction)
          .filter(Boolean) || [];

      if (type === "depart") setSuggestDepart(predictions);
      else setSuggestArrivee(predictions);
    } catch (e) {
      console.error("Erreur suggestions Google:", e);
      if (type === "depart") setSuggestDepart([]);
      else setSuggestArrivee([]);
    }
  };

  // selection => Place Details via toPlace().fetchFields(...)
  const selectPrediction = async (prediction, type) => {
    if (!prediction) return;

    try {
      const place = prediction.toPlace();
      await place.fetchFields({
        fields: ["location", "formattedAddress", "displayName"],
      });

      if (!place.location) return;

      const coords = [place.location.lat(), place.location.lng()];
      const label =
        place.formattedAddress ||
        place.displayName ||
        labelFromPrediction(prediction);

      if (type === "depart") {
        setDepart(label);
        setCoordsDepart(coords);
        setSuggestDepart([]);
        tokenDepartRef.current = null; // reset token après sélection
      } else {
        setArrivee(label);
        setCoordsArrivee(coords);
        setSuggestArrivee([]);
        tokenArriveeRef.current = null;
      }
    } catch (e) {
      console.error("Erreur Place Details:", e);
    }
  };

  // OSRM route
  const computeRoute = async () => {
    if (!coordsDepart || !coordsArrivee) return;

    const [lat1, lon1] = coordsDepart;
    const [lat2, lon2] = coordsArrivee;

    const url = `https://router.project-osrm.org/route/v1/driving/${lon1},${lat1};${lon2},${lat2}?overview=full&geometries=geojson`;
    const res = await fetch(url);
    const data = await res.json();

    if (data.routes && data.routes[0]) {
      const r = data.routes[0];
      const poly = r.geometry.coordinates.map(([lon, lat]) => [lat, lon]);
      setRoute(poly);

      const km = r.distance / 1000;
      const minutes = r.duration / 60;
      setDistance(km.toFixed(1));
      setDuree(minutes.toFixed(0));
      setPrix((km * TARIF_KM).toFixed(2));
    }
  };

  useEffect(() => {
    computeRoute();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [coordsDepart, coordsArrivee]);

  const handleSubmit = (e) => {
    e.preventDefault();

    if (!depart || !arrivee)
      return alert("Veuillez saisir un départ et une arrivée.");
    if (!date) return alert("Veuillez sélectionner une date.");
    if (!heure) return alert("Veuillez sélectionner une heure.");

    setBooking({
      name,
      firstname,
      email,
      phone,
      depart,
      arrivee,
      date,
      heure,
      serviceType,
      options,
      passagers,
      bagages,
      distance,
      duree,
      prix,
      demandes,
    });
    setShowRecap(true);
  };

  return (
    <Container fluid className="reservation-page p-4">
      <img src={logo} alt="AzurPac VTC" className="resa-logo-large-2" />
      <button
        className="close-btn"
        onClick={() => (window.location.href = "/")}
      >
        ×
      </button>
      <div className="reservation-header">
        <h2 className="title">Réservation</h2>
      </div>

      <p className="map-intro-text">
        Visualisez votre trajet estimé en temps réel avec distance et tarif
      </p>

      <Row className="mt-4">
        {/* FORMULAIRE */}
        <Col lg={5} className="mb-4">
          <Form onSubmit={handleSubmit} className="form-dark rounded shadow">
            {step === 1 && (
              <div className="form-section">
                <h5 className="form-section-title">Informations passager</h5>

                <div className="d-flex gap-3">
                  <Form.Group className="mb-3 flex-fill">
                    <Form.Label>Nom</Form.Label>
                    <Form.Control
                      value={name}
                      onChange={(e) => setName(e.target.value)}
                      required
                    />
                  </Form.Group>

                  <Form.Group className="mb-3 flex-fill">
                    <Form.Label>Prénom</Form.Label>
                    <Form.Control
                      value={firstname}
                      onChange={(e) => setFirstname(e.target.value)}
                      required
                    />
                  </Form.Group>
                </div>

                <Form.Group className="mb-3">
                  <Form.Label>Email</Form.Label>
                  <Form.Control
                    type="email"
                    value={email}
                    onChange={(e) => setEmail(e.target.value)}
                    required
                  />
                </Form.Group>

                <Form.Group className="mb-3">
                  <Form.Label>Téléphone</Form.Label>
                  <Form.Control
                    type="tel"
                    value={phone}
                    onChange={(e) => setPhone(e.target.value)}
                    required
                  />
                </Form.Group>

                <div className="d-flex gap-3">
                  <Form.Group className="mb-3 flex-fill">
                    <Form.Label>Date prise en charge</Form.Label>
                    <Form.Control
                      type="date"
                      value={date}
                      min={new Date().toISOString().split("T")[0]}
                      onChange={(e) => setDate(e.target.value)}
                      required
                    />
                  </Form.Group>

                  <Form.Group className="mb-3 flex-fill">
                    <Form.Label>Heure</Form.Label>
                    <Form.Control
                      type="time"
                      value={heure}
                      onChange={(e) => setHeure(e.target.value)}
                      required
                    />
                  </Form.Group>
                </div>

                <div className="d-flex gap-3">
                  <Form.Group className="mb-3 flex-fill">
                    <Form.Label>Passagers</Form.Label>
                    <Form.Control
                      type="number"
                      min={1}
                      max={4}
                      value={passagers}
                      onChange={(e) => setPassagers(e.target.value)}
                    />
                  </Form.Group>

                  <Form.Group className="mb-3 flex-fill">
                    <Form.Label>Bagages</Form.Label>
                    <Form.Control
                      type="number"
                      min={0}
                      max={6}
                      value={bagages}
                      onChange={(e) => setBagages(e.target.value)}
                    />
                  </Form.Group>

                  <Form.Group className="mb-3 flex-fill">
                    <Form.Label>Service</Form.Label>
                    <Form.Select
                      value={serviceType}
                      onChange={(e) => setServiceType(e.target.value)}
                    >
                      <option>Business</option>
                      <option>Transfert Aéroport</option>
                      <option>Loisirs</option>
                    </Form.Select>
                  </Form.Group>
                </div>

                {formError && (
                  <div className="form-error mt-2">{formError}</div>
                )}

                <Button
                  type="button"
                  className="btn-primary-gold w-100 mt-2"
                  onClick={() => {
                    if (
                      !name ||
                      !firstname ||
                      !email ||
                      !phone ||
                      !date ||
                      !heure
                    ) {
                      setFormError(
                        "Veuillez remplir tous les champs obligatoires.",
                      );
                      return;
                    }
                    setFormError("");
                    setStep(2);
                  }}
                >
                  Suivant
                </Button>
              </div>
            )}

            {step === 2 && (
              <div className="form-section">
                <h5 className="form-section-title">Trajet & options</h5>

                {/* Autocomplete Départ */}
                <Form.Group className="mb-3 position-relative">
                  <Form.Label>Adresse de départ</Form.Label>
                  <Form.Control
                    value={depart}
                    onChange={(e) => {
                      const value = e.target.value;
                      setDepart(value);

                      if (debounceDepart.current)
                        clearTimeout(debounceDepart.current);
                      debounceDepart.current = setTimeout(
                        () => fetchSuggestions(value, "depart"),
                        DEBOUNCE_DELAY,
                      );
                    }}
                    placeholder={
                      placesReady ? "Ex: Nice Aéroport" : "Chargement Google..."
                    }
                    required
                  />
                  {suggestDepart.length > 0 && (
                    <div className="autocomplete-dropdown">
                      {suggestDepart.map((p, idx) => (
                        <div
                          key={p.placeId || idx}
                          className="autocomplete-item"
                          onClick={() => selectPrediction(p, "depart")}
                        >
                          {labelFromPrediction(p)}
                        </div>
                      ))}
                    </div>
                  )}
                </Form.Group>

                {/* Autocomplete Arrivée */}
                <Form.Group className="mb-3 position-relative">
                  <Form.Label>Adresse d'arrivée</Form.Label>
                  <Form.Control
                    value={arrivee}
                    onChange={(e) => {
                      const value = e.target.value;
                      setArrivee(value);

                      if (debounceArrivee.current)
                        clearTimeout(debounceArrivee.current);
                      debounceArrivee.current = setTimeout(
                        () => fetchSuggestions(value, "arrivee"),
                        DEBOUNCE_DELAY,
                      );
                    }}
                    placeholder={
                      placesReady ? "Ex: Monaco Casino" : "Chargement Google..."
                    }
                    required
                  />
                  {suggestArrivee.length > 0 && (
                    <div className="autocomplete-dropdown">
                      {suggestArrivee.map((p, idx) => (
                        <div
                          key={p.placeId || idx}
                          className="autocomplete-item"
                          onClick={() => selectPrediction(p, "arrivee")}
                        >
                          {labelFromPrediction(p)}
                        </div>
                      ))}
                    </div>
                  )}
                </Form.Group>

                <Form.Group className="mb-3">
                  <Form.Label>Demandes spécifiques</Form.Label>
                  <Form.Control
                    as="textarea"
                    rows={3}
                    placeholder="Siège bébé, chauffeur bilingue, stop intermédiaire..."
                    value={demandes}
                    onChange={(e) => setDemandes(e.target.value)}
                  />
                </Form.Group>

                <Form.Group className="mb-3 options-checks">
                  <Form.Check
                    type="checkbox"
                    label="WiFi"
                    checked={options.wifi}
                    onChange={() =>
                      setOptionsState({ ...options, wifi: !options.wifi })
                    }
                  />
                  <Form.Check
                    type="checkbox"
                    label="Café"
                    checked={options.cafe}
                    onChange={() =>
                      setOptionsState({ ...options, cafe: !options.cafe })
                    }
                  />
                  <Form.Check
                    type="checkbox"
                    label="Eau"
                    checked={options.eau}
                    onChange={() =>
                      setOptionsState({ ...options, eau: !options.eau })
                    }
                  />
                </Form.Group>

                <div className="mt-3 d-flex gap-2 stepper-actions">
                  <Button
                    variant="outline-secondary"
                    type="button"
                    onClick={() => setStep(1)}
                    className="flex-fill d-flex align-items-center justify-content-center"
                    style={{ height: 46 }}
                  >
                    Précédent
                  </Button>

                  <Button
                    type="submit"
                    className="btn-primary-gold flex-fill d-flex align-items-center justify-content-center"
                    style={{ height: 46 }}
                  >
                    Réserver
                  </Button>
                </div>
              </div>
            )}
          </Form>
        </Col>

        {/* CARTE */}
        <Col lg={7}>
          <div className="map-wrapper">
            <MapContainer
              center={coordsDepart || [43.7102, 7.262]}
              zoom={10}
              scrollWheelZoom={false}
            >
              <TileLayer
                url="https://{s}.tile.openstreetmap.org/{z}/{x}/{y}.png"
                attribution="&copy; OpenStreetMap"
              />

              {coordsDepart && (
                <Marker position={coordsDepart} icon={departIcon}>
                  <Popup>Départ: {depart}</Popup>
                </Marker>
              )}
              {coordsArrivee && (
                <Marker position={coordsArrivee} icon={departIcon}>
                  <Popup>Arrivée: {arrivee}</Popup>
                </Marker>
              )}

              {route && (
                <>
                  <Polyline positions={route} color="#3399ff" weight={5} />
                  <FitToRoute route={route} />

                  <Popup
                    ref={popupRef}
                    position={coordsArrivee}
                    className="popup-info"
                  >
                    <div>
                      <strong>Distance:</strong> {distance} km
                      <br />
                      <strong>Durée:</strong> {duree} min
                      <br />
                      <strong>Prix estimé:</strong> {prix} €<br />
                      <strong>Service:</strong> {serviceType}
                    </div>
                  </Popup>

                  <AutoOpenPopup popupRef={popupRef} />
                </>
              )}
            </MapContainer>
          </div>
        </Col>
      </Row>

      <RecapModal
        booking={booking}
        show={showRecap}
        onClose={() => setShowRecap(false)}
        onConfirm={() => alert("Réservation confirmée")}
      />
    </Container>
  );
}
